import { useForm } from '@tanstack/react-form';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { z, ZodError } from 'zod';

import styles from '../styles.module.css';
import Button from '@/components/LoginComponents/Button';
import Input from '@/components/LoginComponents/Input';
import { POOL_NAME } from '@/constants';
import { useModal } from '@/context/ModalContext';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { clearStatus, fetchForgot } from '@/slices/authSlice';
import { getUserProfile, initialStateUser } from '@/slices/userProfileSlice';
import { RootState } from '@/store';

const forgotPasswordSchema = z.object({
  email: z.string().min(1, 'Email should not be empty')
});

function ForgotPasswordContainer() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { updateModalContent, closeModal } = useModal();

  const [isLoading, setIsLoading] = useState(false);
  const auth = useSelector((state: RootState) => state.auth);
  const errorResponse = auth?.error?.description;

  const form = useForm({
    defaultValues: {
      email: ''
    },
    validators: {
      onSubmit: forgotPasswordSchema
    },
    onSubmit: async ({ value }) => {
      try {
        setIsLoading(true);
        await dispatch(
          fetchForgot({
            username: value.email,
            poolName: POOL_NAME
          })
        );
      } finally {
        setIsLoading(false);
      }
    }
  });

  useEffect(() => {
    if (auth?.status?.code === 'forgotSuccess' && !isLoading) {
      updateModalContent(null, {
        modalTitle: 'Password Recovery',
        modalButtons: [
          {
            label: 'OK',
            onClick: () => {
              dispatch(clearStatus());
              dispatch(
                getUserProfile({
                  ...initialStateUser,
                  email: form.getFieldValue('email')
                })
              );
              navigate('/password-recovery');
              closeModal();
            },
            variant: 'okBlue'
          }
        ],
        alignButtons: 'center',
        contentTitle: auth?.status?.message
      });
    }
  }, [auth?.status?.message, isLoading]);

  useEffect(() => {
    dispatch(clearStatus());
  }, [dispatch]);

  return (
    <div className={styles.form}>
      {errorResponse && (
        <div className={styles.formErrors}>{errorResponse}</div>
      )}
      <h3>Password Recovery</h3>
      <form.Field name="email">
        {field => (
          <Input
            type="text"
            placeholder="Email / Username"
            value={field.state.value}
            onChange={value => field.handleChange(value || '')}
            errors={field?.state?.meta?.errors as ZodError[]}
          />
        )}
      </form.Field>

      <Button
        label="SUBMIT"
        isLoading={isLoading}
        onClick={() => form.handleSubmit()}
      />
    </div>
  );
}

export default ForgotPasswordContainer;
